import { InvestorDetailsActivityTabListEntityTransactionsQuery } from '@/__generated__/graphql';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import { TableBodyWrapper, TableRowCell, TableRowWrapper, TextCell } from '@/components/table/styles';
import { currencyFormatter, formatCash } from '@/shared/utils/currency';
import { getUTCDate } from '@/shared/utils/date';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import { InvestorDetailsEntityTypeEnum } from '../../../types';
import { activityTableColumns } from '../constants';

interface ActivityTableBodyProps {
  tableRows: InvestorDetailsActivityTabListEntityTransactionsQuery['ListEntityTransactions']['data'];
  entityType: InvestorDetailsEntityTypeEnum;
  openOpportunityDetailsPage: (id: string) => void;
}

const ActivityTableBody = ({ tableRows, entityType, openOpportunityDetailsPage }: ActivityTableBodyProps) => {
  if (!tableRows?.length) {
    return <NoResults />;
  }

  const amountToolTipContent = (row: InvestorDetailsActivityTabListEntityTransactionsQuery['ListEntityTransactions']['data'][number]) => {
    return (
      <TableBodyWrapper>
        <TableRowCell width={100}>
          <TextCell>Investment Amount: {currencyFormatter.format(row.amount)}</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>Total Amount incl Fees: {currencyFormatter.format(row.totalAmount)}</TextCell>
        </TableRowCell>
      </TableBodyWrapper>
    );
  };

  return (
    <>
      {tableRows.map((row) => (
        <TableRowWrapper key={guid()}>
          <TableRowCell width={activityTableColumns[0].width}>
            <TextCell>{getUTCDate(row.date)}</TextCell>
          </TableRowCell>
          <CustomRowCell width={activityTableColumns[1].width}>
            <RedirectTitleCell id="actionCurioText" onClick={() => openOpportunityDetailsPage(row.investmentId)}>
              {row.strategy}
            </RedirectTitleCell>
            {(entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group) && (
              <EntityName>{row.entityName}</EntityName>
            )}
          </CustomRowCell>
          <TableRowCell width={activityTableColumns[2].width}>
            <TextCell>{row.transactionType}</TextCell>
          </TableRowCell>
          <TableRowCell width={activityTableColumns[3].width}>
            <TextCell>
              <CellWithTooltip>
                {formatCash(row.amount)}
                <TooltipWrapper id="infoWrap">
                  <Tooltip title={amountToolTipContent(row)}>
                    <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                  </Tooltip>
                </TooltipWrapper>
              </CellWithTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={activityTableColumns[4].width}>
            <TextCell>
              <FormattingTooltip zIndex={1000}>{row.distributionsToDate}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={activityTableColumns[5].width}>
            <TextCell>
              <FormattingTooltip zIndex={1000}>{row.remainingCapital}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={activityTableColumns[5].width}>
            <TextCell>
              <FormattingTooltip zIndex={1000}>{row.marketValue}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
        </TableRowWrapper>
      ))}
    </>
  );
};

export default ActivityTableBody;

const CustomRowCell = styled(TableRowCell)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 0;
`;

const EntityName = styled(TextCell)`
  color: ${themeVar('font.strong')};
`;

const TooltipWrapper = styled.div`
  opacity: 0;
`;

const CellWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
